import { useCallback, useEffect, useMemo, useState } from "react";
import type { SignPrefillPathEntry, TemplateFamily, TemplateVersion } from "@/lib/sign/signTemplateApiClient";
import {
  getSignPrefillRegistry,
  getSignTemplatePdfUrl,
  publishSignTemplate,
  upsertSignTemplate,
  uploadSignTemplatePdf,
} from "@/lib/sign/signTemplateApiClient";
import {
  clampFieldToPage,
  DEFAULT_SIGNER_ROLE_PRESETS,
  FIELD_KIND_DEFAULTS,
  fieldKindUsesDataPath,
  fieldKindUsesSignerRole,
  newEditorFieldId,
  serializeEditorFieldMap,
  type EditorField,
  type EditorFieldMap,
  type EditorSignerRole,
  type TemplateFieldKind,
} from "@/lib/sign/templateEditorModel";
import { SignTemplatePdfPage } from "./SignTemplatePdfPage";
import { SignTemplateSignersPanel } from "./SignTemplateSignersPanel";

type Props = {
  family: TemplateFamily;
  version: TemplateVersion;
  subscribedReadOnly?: boolean;
  onSaved: (family: TemplateFamily, version: TemplateVersion) => void;
  onClose: () => void;
};

const FIELD_KINDS: TemplateFieldKind[] = ["signature", "initials", "date", "text", "checkbox"];
const DOC_BRIDGE_FIELD_KINDS: TemplateFieldKind[] = ["signature", "initials", "date"];

export function SignTemplateEditor({ family, version, subscribedReadOnly, onSaved, onClose }: Props) {
  const readOnly = version.status !== "draft" || subscribedReadOnly === true;
  const signaturePlacementOnly = Boolean(version.source_doc_version_id);
  const addableFieldKinds = signaturePlacementOnly ? DOC_BRIDGE_FIELD_KINDS : FIELD_KINDS;
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [preview, setPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fieldMap, setFieldMap] = useState<EditorFieldMap>(
    () => version.field_map ?? { fields: [] }
  );
  const [signers, setSigners] = useState<EditorSignerRole[]>(
    () => (version.signer_roles?.length ? version.signer_roles : DEFAULT_SIGNER_ROLE_PRESETS)
  );
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [prefillPaths, setPrefillPaths] = useState<SignPrefillPathEntry[]>([]);
  const [sampleFixture, setSampleFixture] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const selectedField = useMemo(
    () => fieldMap.fields.find((f) => f.id === selectedFieldId) ?? null,
    [fieldMap.fields, selectedFieldId]
  );

  const loadPdf = useCallback(async () => {
    const r = await getSignTemplatePdfUrl(version.id);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setPdfUrl(r.data.signed_url);
  }, [version.id]);

  useEffect(() => {
    void loadPdf();
  }, [loadPdf]);

  useEffect(() => {
    void (async () => {
      const r = await getSignPrefillRegistry();
      if (!r.ok) return;
      setPrefillPaths(r.data.paths);
      setSampleFixture(r.data.sample_fixture);
    })();
  }, []);

  function addField(kind: TemplateFieldKind) {
    if (readOnly) return;
    const defaults = FIELD_KIND_DEFAULTS[kind];
    const primarySigner = signers[0]?.role ?? "customer";
    const field: EditorField = {
      id: newEditorFieldId(kind),
      kind,
      page,
      x: 72,
      y: 120 + fieldMap.fields.filter((f) => f.page === page).length * (defaults.height + 8),
      width: defaults.width,
      height: defaults.height,
      signerRole: fieldKindUsesSignerRole(kind) ? primarySigner : undefined,
      required: true,
    };
    setFieldMap((prev) => ({ fields: [...prev.fields, field] }));
    setSelectedFieldId(field.id);
  }

  function updateField(id: string, patch: Partial<EditorField>) {
    setFieldMap((prev) => ({
      fields: prev.fields.map((f) => (f.id === id ? clampFieldToPage({ ...f, ...patch }, 612, 792) : f)),
    }));
  }

  function removeField(id: string) {
    setFieldMap((prev) => ({ fields: prev.fields.filter((f) => f.id !== id) }));
    if (selectedFieldId === id) setSelectedFieldId(null);
  }

  async function saveDraft() {
    setBusy(true);
    setError(null);
    setMessage(null);
    const r = await upsertSignTemplate({
      version_id: version.id,
      family_id: family.id,
      name: family.name,
      document_class: family.document_class,
      sale_path: family.sale_path,
      field_map: serializeEditorFieldMap(fieldMap),
      signer_roles: signers.map((s, i) => ({ ...s, order: i })),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setMessage("Draft saved.");
    onSaved(r.data.family, r.data.version);
  }

  async function publishDraft() {
    setBusy(true);
    setError(null);
    await saveDraft();
    const r = await publishSignTemplate(version.id);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setMessage("Template published.");
    onSaved(r.data.family, r.data.version);
  }

  async function handlePdfUpload(file: File) {
    if (readOnly) return;
    setBusy(true);
    setError(null);
    const uploadReq = await upsertSignTemplate({
      version_id: version.id,
      request_pdf_upload: { byte_size: file.size },
    });
    if (!uploadReq.ok || !uploadReq.data.pdf_upload) {
      setBusy(false);
      setError(uploadReq.ok ? "Upload URL not returned" : uploadReq.error);
      return;
    }
    const up = uploadReq.data.pdf_upload;
    const uploaded = await uploadSignTemplatePdf({
      signedUrl: up.signed_url,
      token: up.token,
      file,
    });
    if (!uploaded.ok) {
      setBusy(false);
      setError(uploaded.error);
      return;
    }
    const committed = await upsertSignTemplate({
      version_id: version.id,
      pdf_commit: {
        instance_id: version.id,
        artifact_kind: "template_source",
        artifact_id: up.artifact_id,
        object_path: up.path,
        byte_size: file.size,
      },
    });
    setBusy(false);
    if (!committed.ok) {
      setError(committed.error);
      return;
    }
    setMessage("PDF uploaded.");
    onSaved(committed.data.family, committed.data.version);
    void loadPdf();
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">{family.name}</h3>
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            v{version.version_number} · {version.status}
            {signaturePlacementOnly ? " · from Document Builder" : ""}
            {subscribedReadOnly ? " · subscribed publisher template" : ""}
            {readOnly ? " (read-only)" : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1.5 text-xs dark:border-zinc-600">
            Back
          </button>
          {!readOnly ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void saveDraft()}
                className="rounded bg-gray-800 px-3 py-1.5 text-xs text-white disabled:opacity-50"
              >
                Save draft
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void publishDraft()}
                className="rounded bg-indigo-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
              >
                Publish
              </button>
            </>
          ) : null}
        </div>
      </header>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-100">
          {error}
        </div>
      ) : null}
      {message ? <p className="text-sm text-emerald-700 dark:text-emerald-300">{message}</p> : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {!readOnly ? (
              <>
                {addableFieldKinds.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => addField(kind)}
                    className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-zinc-600"
                  >
                    + {FIELD_KIND_DEFAULTS[kind].label}
                  </button>
                ))}
                {!signaturePlacementOnly ? (
                  <label className="ml-auto text-xs">
                    PDF
                    <input
                      type="file"
                      accept="application/pdf"
                      className="ml-1 text-xs"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handlePdfUpload(f);
                      }}
                    />
                  </label>
                ) : null}
              </>
            ) : null}
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={preview} onChange={(e) => setPreview(e.target.checked)} />
              Preview sample values
            </label>
            <label className="text-xs">
              Page
              <input
                type="number"
                min={1}
                max={pageCount}
                value={page}
                onChange={(e) => setPage(Math.max(1, Number(e.target.value) || 1))}
                className="ml-1 w-14 rounded border px-1 dark:border-zinc-700 dark:bg-zinc-800"
              />
            </label>
            <label className="text-xs">
              Zoom
              <input
                type="range"
                min={0.7}
                max={1.6}
                step={0.05}
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="ml-1 align-middle"
              />
            </label>
          </div>

          {pdfUrl ? (
            <SignTemplatePdfPage
              pdfUrl={pdfUrl}
              page={page}
              scale={scale}
              fields={fieldMap.fields}
              selectedFieldId={selectedFieldId}
              preview={preview}
              sampleFixture={sampleFixture}
              readOnly={readOnly}
              onPageCount={setPageCount}
              onSelectField={setSelectedFieldId}
              onMoveField={(id, patch) => updateField(id, patch)}
            />
          ) : (
            <p className="text-sm text-gray-500">
              {signaturePlacementOnly
                ? "Loading PDF from Document Builder export…"
                : "Upload a PDF to begin placing fields."}
            </p>
          )}
        </div>

        <aside className="space-y-3">
          <SignTemplateSignersPanel signers={signers} readOnly={readOnly} onChange={setSigners} />

          {selectedField ? (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Selected field</h4>
              <p className="text-xs text-gray-600 dark:text-zinc-300">
                {selectedField.id} · {selectedField.kind}
              </p>
              {fieldKindUsesSignerRole(selectedField.kind) ? (
                <label className="block text-xs text-gray-500">
                  Signer role
                  <select
                    disabled={readOnly}
                    value={selectedField.signerRole ?? signers[0]?.role ?? "customer"}
                    onChange={(e) => updateField(selectedField.id, { signerRole: e.target.value })}
                    className="mt-1 w-full rounded border px-2 py-1 dark:border-zinc-700 dark:bg-zinc-800"
                  >
                    {signers.map((s) => (
                      <option key={s.role} value={s.role}>
                        {s.label ?? s.role}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {fieldKindUsesDataPath(selectedField.kind) ? (
                <label className="block text-xs text-gray-500">
                  Data path
                  <select
                    disabled={readOnly}
                    value={selectedField.dataPath ?? ""}
                    onChange={(e) =>
                      updateField(selectedField.id, { dataPath: e.target.value || undefined })
                    }
                    className="mt-1 w-full rounded border px-2 py-1 dark:border-zinc-700 dark:bg-zinc-800"
                  >
                    <option value="">— static / manual —</option>
                    {prefillPaths.map((p) => (
                      <option key={p.path} value={p.path}>
                        {p.label} ({p.path})
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => removeField(selectedField.id)}
                  className="text-xs text-red-600"
                >
                  Remove field
                </button>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
